import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { clientsApi, extractOne } from '../../api/facades';
import { clientTypeLabel, clientDisplayName, clientAvatarInitials } from './clientHelpers';

const ClientDetailPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = clientId ? parseInt(clientId, 10) : NaN;
  const [showDelete, setShowDelete] = React.useState(false);

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['clients', numId],
    queryFn: () => clientsApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  const deleteMu = useMutation({
    mutationFn: () => clientsApi.remove(numId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowDelete(false);
      navigate('/dashboard/clients');
      toast.success('Client deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  if (!clientId || Number.isNaN(numId) || isError || (!isLoading && !row)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200 mb-2">Client not found</h1>
        <Button variant="secondary" onClick={() => navigate('/dashboard/clients')}>Back to Clients</Button>
      </div>
    );
  }

  const r = row as any;
  const displayName = clientDisplayName(r);
  const initials = clientAvatarInitials(r);
  const people = r?.people;
  const fromPeople =
    people?.full_name ??
    (people ? [people.first_name, people.middle_name, people.last_name].filter(Boolean).join(' ') : null);
  const mainContactName = (fromPeople || r?.full_name || '').trim() || '—';
  const mainContactEmail = r?.people?.email ?? r?.email ?? '—';
  const mainContactPosition = r?.main_contact_position ?? '—';
  const mainContactMobile = r?.people?.mobile ?? r?.mobile ?? r?.phone ?? '—';
  const companyAddress = r?.company_address ?? r?.address ?? '—';

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-lg font-semibold text-white">
            {initials}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">{displayName || 'Client'}</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-0.5 text-sm">{clientTypeLabel(r?.client_type)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard/clients')}>BACK</Button>
          <Button variant="secondary" onClick={() => navigate(`/dashboard/clients/${clientId}/edit`)}>Edit</Button>
          <Button variant="outline" onClick={() => setShowDelete(true)}>Delete</Button>
        </div>
      </div>

      <div className="mt-8 border-t border-gray-100 dark:border-gray-700 pt-6">
        <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Main contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Name</div>
            <div className="text-slate-700 dark:text-slate-200">{mainContactName}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Email</div>
            <div className="text-slate-700 dark:text-slate-200">{mainContactEmail}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Position</div>
            <div className="text-slate-700 dark:text-slate-200">{mainContactPosition}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Mobile</div>
            <div className="text-slate-700 dark:text-slate-200">{mainContactMobile}</div>
          </div>
        </div>
      </div>

      <div className="mt-6 border-t border-gray-100 dark:border-gray-700 pt-6">
        <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Company address</h2>
        <div className="text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{companyAddress}</div>
      </div>

      <ConfirmDeleteModal open={showDelete} onClose={() => setShowDelete(false)} onConfirm={() => deleteMu.mutate()} title="Delete client" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default ClientDetailPage;
